import React, { useEffect, useState } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { PlusIcon, PencilIcon, MapPin, Download } from "lucide-react";
import { MainHeading } from "@/components/common/MainHeading";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { IFilter } from "@/interfaces/common.interface";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDateTime, GetFilterUrl, downloadExcel } from "@/utility/helper";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RoutesLink } from "@/utility/RouteLinks";
import { TableFilters } from "@/components/common/TableFilters";
import { useNavigate } from "react-router-dom";
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { IZone, IZoneListResponse } from '@/interfaces/Zone/zone.interface';
import { Switch } from '@/components/ui/switch';

export const Zones = () => {
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const { canCreate, canEdit, canExport } = usePermissions();

    const [zones, setZones] = useState<IZone[]>([]);
    const [loading, setLoading] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo } = useFilter<IFilter>({
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort
        });
        const url = `${API_URL + API_ENDPOINT.zones}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'zones_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const fetchZones = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IZoneListResponse>(
                `${API_URL}${API_ENDPOINT.zones}?${queryParams}`,
                { method: 'GET' }
            );
            setZones(response.data?.zones || []);
            setPageInfo(
                response.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 },
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchZones();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search,]);

    const handleStatusToggle = async (id: string, status: string) => {
        try {
            const response = await callApi(
                `${API_URL}${API_ENDPOINT.zones}/${id}`,
                { method: 'PATCH', data: { status: status === 'active' ? 'inactive' : 'active' } }
            );
            setZones(zones.map((zone) => zone.id === id ? { ...zone, status: response?.data?.status } : zone));
        } catch (error) {
            ThrowError(error);
        }
    }

    const columns: TableColumn<IZone>[] = [
        {
            key: "name",
            header: "Zone Details",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                        <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="font-semibold text-foreground text-sm">{item.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">
                            {item.description || 'No description provided'}
                        </p>
                    </div>
                </div>
            ),
        },
        {
            key: "location",
            header: "Location Coverage",
            render: (item) => (
                <div className="text-sm">
                    <p className="font-medium text-foreground">{item.city || '—'}</p>
                    <p className="text-xs text-muted-foreground">{item.area || '—'}</p>
                </div>
            ),
        },
        {
            key: "coordinates",
            header: "Coordinates",
            render: (item) => (
                <div className="text-xs text-muted-foreground font-mono">
                    {item.latitude}, {item.longitude}
                </div>
            ),
        },
        {
            key: "createdAt",
            header: "Created On",
            render: (item) => (
                <div className="text-muted-foreground text-xs">{formatDateTime(item.createdAt)}</div>
            ),
        },
        {
            key: "status",
            header: "Status",
            render: (item) => {
                // Only owner or admin can toggle status
                // const canToggle = user?.id === item.store?.userId;

                return (
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={item.status === 'active'}
                            onCheckedChange={() => handleStatusToggle(item.id, item.status)}
                            // disabled={updatingId === item.id || !canToggle || !canDo}
                            title={"Toggle status"}
                        />
                        <StatusBadge status={item.status} />
                    </div>
                )
            },
        },
    ];

    const actions: Action<IZone>[] = [];

    if (canEdit(RESOURCES.ZONE)) {
        actions.push({
            text: 'Edit Zone',
            icon: <PencilIcon strokeWidth={1.5} size={16} className="cursor-pointer" />,
            onClick: (record: IZone) => {
                navigate(prefix + RoutesLink.EditZone.replace(':id', record.id));
            },
        });
    }

    const activeCount = zones.filter(z => z.status === 'ACTIVE').length;
    const inactiveCount = zones.filter(z => z.status !== 'ACTIVE').length;

    return (
        <Layout>
            <MainHeading
                title="Geographic Zones"
                description={`${pageInfo.total} total zones · ${activeCount} active · ${inactiveCount} inactive`}
                {...(canCreate(RESOURCES.ZONE) && {
                    actionLabel: 'Add New Zone',
                    actionIcon: <PlusIcon className="w-4 h-4" />,
                    onAction: () => navigate(prefix + RoutesLink.AddZone),
                })}
            />
            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search zones by name or city..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.ZONE) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>
                <CommonTable
                    columns={columns}
                    data={zones}
                    loading={loading}
                    emptyMessage="No geographic zones configured yet."
                    actions={actions}
                />
                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                />
            </div>
        </Layout>
    );
};

export default Zones;
